import { useMobile } from '../hooks/useMobile';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import NotificationBanner from '../components/NotificationBanner';
import RAGBadge from '../components/RAGBadge';
import KanbanBoard from '../components/KanbanBoard';
import { usePermissions } from '../hooks/usePermissions';

const token = () => localStorage.getItem('token');

const COMMISSION_STAGES = ['all', 'pipeline', 'payable', 'paid', 'cancelled'];
const BOARD_LIMIT = 100;

const STAGE_META = {
  pipeline:  { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b' },
  payable:   { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  paid:      { bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
};

function StageBadge({ stage }) {
  const m = STAGE_META[stage] || { bg: '#f3f4f6', color: '#6b7280', dot: '#9ca3af' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      padding: '3px 9px', borderRadius: 20, background: m.bg, color: m.color }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, display: 'inline-block' }} />
      {stage}
    </span>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px',
      border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}`,
      display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0e1b2c', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

function ViewToggle({ viewMode, setViewMode }) {
  const btn = (mode, label, icon) => (
    <button onClick={() => setViewMode(mode)}
      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', border: 'none', borderRadius: 7,
        background: viewMode === mode ? '#fff' : 'transparent',
        color: viewMode === mode ? '#0e1b2c' : '#6b7280',
        boxShadow: viewMode === mode ? '0 1px 2px rgba(0,0,0,0.08)' : 'none' }}>
      <span>{icon}</span>{label}
    </button>
  );
  return (
    <div style={{ display: 'flex', gap: 2, background: '#f3f4f6', borderRadius: 9, padding: 3 }}>
      {btn('list', 'List', '☰')}
      {btn('board', 'Board', '▦')}
    </div>
  );
}

export default function DealsListPage({ noLayout = false } = {}) {
  const mobile = useMobile();
  const navigate = useNavigate();
  const perms = usePermissions();

  const [deals, setDeals] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [introducers, setIntroducers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState('');

  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [introFilter, setIntroFilter] = useState('all');
  const [dealStageFilter, setDealStageFilter] = useState('all');
  const [workflowStages, setWorkflowStages] = useState([]);
  const [showArchived, setShowArchived] = useState(false);

  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('dealsViewMode') === 'board' ? 'board' : 'list'; }
    catch { return 'list'; }
  });

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const LIMIT = 50;

  useEffect(() => {
    try { localStorage.setItem('dealsViewMode', viewMode); } catch { /* noop */ }
  }, [viewMode]);

  const loadDeals = useCallback(async (p = page) => {
    setLoading(true); setError(null);
    try {
      const effectiveLimit = viewMode === 'board' ? BOARD_LIMIT : LIMIT;
      const params = new URLSearchParams({ page: viewMode === 'board' ? 1 : p, limit: effectiveLimit });
      if (search.trim())         params.set('search', search.trim());
      if (stageFilter !== 'all') params.set('stage', stageFilter);
      if (introFilter !== 'all') params.set('introducer_id', introFilter);
      if (dealStageFilter !== 'all') params.set('deal_stage_id', dealStageFilter);
      if (showArchived)          params.set('include_archived', 'true');

      const [dr, ir] = await Promise.all([
        fetch(`/api/deals?${params}`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch('/api/introducers',       { headers: { Authorization: `Bearer ${token()}` } }),
      ]);
      const dj = await dr.json();
      const ij = await ir.json();
      setDeals(dj.deals || []);
      setFiltered(dj.deals || []);
      setMeta(dj.meta || { total: 0, pages: 1 });
      setScope(dj.scope || '');
      setIntroducers(ij.introducers || []);
    } catch { setError('Failed to load deals.'); }
    finally { setLoading(false); }
  }, [page, search, stageFilter, introFilter, dealStageFilter, showArchived, viewMode]);

  useEffect(() => {
    fetch('/api/stages', { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : { stages: [] })
      .then(d => setWorkflowStages(d.stages || d || []))
      .catch(() => {});
  }, []);

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (search.trim())         params.set('search', search.trim());
    if (stageFilter !== 'all') params.set('stage', stageFilter);
    if (introFilter !== 'all') params.set('introducer_id', introFilter);
    if (dealStageFilter !== 'all') params.set('deal_stage_id', dealStageFilter);
    if (showArchived)          params.set('include_archived', 'true');
    try {
      const res = await fetch(`/api/exports/deals?${params}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'deals.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
  };

  useEffect(() => { setPage(1); }, [search, stageFilter, introFilter, dealStageFilter, showArchived]);
  useEffect(() => { loadDeals(page); }, [page, search, stageFilter, introFilter, viewMode]); // eslint-disable-line

  const pipelineCount = deals.filter(d => d.stage === 'pipeline').length;
  const paidCount     = deals.filter(d => d.stage === 'paid').length;
  const totalFee = deals.reduce((s, d) =>
    s + (parseFloat(d.lender_proc_fee) || 0) + (parseFloat(d.client_fee) || 0) - (parseFloat(d.network_fee_amount) || 0), 0);

  const hasFilters = search || stageFilter !== 'all' || introFilter !== 'all' || dealStageFilter !== 'all';

  const _inner = (
    <main style={{ flex: 1, padding: noLayout ? '24px 32px' : (mobile ? '24px 20px' : '32px 40px'), width: '100%', boxSizing: 'border-box' }}>
      <NotificationBanner />

      {/* Header — hidden in noLayout (DealsPage tab bar handles it) */}
      {!noLayout && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#0e1b2c' }}>Deals</h1>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 14 }}>All your active and historical deals</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
            {perms.canEditDeal && (
              <button onClick={() => navigate('/stages')}
                style={{ background: '#fff', border: '1px solid #e5e7eb', color: '#374151', borderRadius: 8, padding: '9px 16px', fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
                🚦 Stages
              </button>
            )}
            {perms.canCreateDeal && (
              <button onClick={() => navigate('/create-deal')}
                style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                + New Deal
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action bar in noLayout mode */}
      {noLayout && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 20, alignItems: 'center' }}>
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          {perms.canEditDeal && (
            <button onClick={() => navigate('/stages')}
              style={{ background: '#fff', border: '1px solid #e5e7eb', color: '#374151', borderRadius: 8, padding: '8px 14px', fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
              🚦 Stages
            </button>
          )}
          {perms.canCreateDeal && (
            <button onClick={() => navigate('/create-deal')}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
              + New Deal
            </button>
          )}
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon="📁" label="Total Deals"  value={deals.length}   accent="#6366f1" />
        <StatCard icon="⚡" label="Pipeline"     value={pipelineCount}  accent="#f59e0b" />
        <StatCard icon="✅" label="Paid"          value={paidCount}      accent="#10b981" />
        <StatCard icon="💷" label="Total Fees"   value={`£${totalFee.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} accent="#0e1b2c" />
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14, pointerEvents: 'none' }}>🔍</span>
            <input type="text" placeholder="Search client, email, phone…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px 8px 34px', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#f9fafb' }} />
          </div>

          <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}
            style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, background: '#f9fafb', cursor: 'pointer' }}>
            {COMMISSION_STAGES.map(s => <option key={s} value={s}>{s === 'all' ? 'All stages' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>

          <select value={introFilter} onChange={e => setIntroFilter(e.target.value)}
            style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, background: '#f9fafb', cursor: 'pointer', maxWidth: 200 }}>
            <option value="all">All introducers</option>
            {introducers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>

          {workflowStages.length > 0 && viewMode === 'list' && (
            <select value={dealStageFilter} onChange={e => setDealStageFilter(e.target.value)}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', fontSize: 13, background: '#f9fafb', cursor: 'pointer', maxWidth: 180 }}>
              <option value="all">All workflow stages</option>
              {workflowStages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}

          <button onClick={() => setShowArchived(v => !v)}
            style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
              border: '1px solid', borderColor: showArchived ? '#0e1b2c' : '#e5e7eb',
              background: showArchived ? '#0e1b2c' : '#f9fafb',
              color: showArchived ? '#fff' : '#6b7280' }}>
            📁 {showArchived ? 'Hide Archived' : 'Archived'}
          </button>

          <button onClick={exportCsv} title="Download the full filtered deal list as CSV"
            style={{ padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              border: '1px solid #d1d5db', background: '#fff', color: '#374151' }}>
            ⬇ Export CSV
          </button>

          {hasFilters && (
            <button onClick={() => { setSearch(''); setStageFilter('all'); setIntroFilter('all'); setDealStageFilter('all'); }}
              style={{ background: 'none', border: 'none', fontSize: 13, color: '#6b7280', cursor: 'pointer', textDecoration: 'underline', padding: '8px 4px' }}>
              Clear
            </button>
          )}
        </div>

        {scope && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
            📋 Scope: <strong>{scope}</strong>
            {hasFilters && <span style={{ marginLeft: 12 }}>Showing {filtered.length} of {deals.length} deals</span>}
            {viewMode === 'board' && meta.total > BOARD_LIMIT && (
              <span style={{ marginLeft: 12, color: '#92400e' }}>⚠ Board shows the first {BOARD_LIMIT} of {meta.total} deals — narrow your filters to see the rest.</span>
            )}
          </div>
        )}
      </div>

      {/* Deal list / board */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 14, color: '#6b7280' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #e5e7eb', borderTopColor: '#10b981', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          <p style={{ margin: 0, fontSize: 14 }}>Loading deals…</p>
        </div>
      ) : error ? (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 18px', color: '#dc2626', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>⚠️ {error}</span>
          <button onClick={loadDeals} style={{ background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '56px 24px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>💼</div>
          <p style={{ fontSize: 16, fontWeight: 600, color: '#0e1b2c', margin: '0 0 6px' }}>{deals.length === 0 ? 'No deals yet' : 'No deals match your filters'}</p>
          <p style={{ fontSize: 13, margin: '0 0 20px' }}>{deals.length === 0 ? 'Create your first deal to get started.' : 'Try adjusting your filters.'}</p>
          {perms.canCreateDeal && deals.length === 0 && (
            <button onClick={() => navigate('/create-deal')}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              + New Deal
            </button>
          )}
        </div>
      ) : viewMode === 'board' ? (
        <KanbanBoard deals={filtered} stages={workflowStages} perms={perms} navigate={navigate} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(deal => {
            const gross = (parseFloat(deal.lender_proc_fee) || 0) + (parseFloat(deal.client_fee) || 0);
            const net   = gross - (parseFloat(deal.network_fee_amount) || 0);
            const hasNet = parseFloat(deal.network_fee_amount || 0) > 0;
            const isArchived = deal.current_status_code === 'archived';
            const notProceeding = deal.current_status_code === 'not_proceeding';

            return (
              <div key={deal.id}
                onClick={() => navigate(`/deal/${deal.id}`)}
                style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                  padding: '16px 20px', cursor: 'pointer', transition: 'all 0.15s',
                  opacity: isArchived ? 0.65 : 1 }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(16,185,129,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

                  {/* Avatar */}
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#0e1b2c',
                    color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                    {(deal.client_name || '?')[0].toUpperCase()}
                  </div>

                  {/* Client info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#0e1b2c', fontSize: 15 }}>{deal.client_name}</span>
                      {deal.reference && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#10b981', background: '#d1fae5', padding: '2px 8px', borderRadius: 9999 }}>
                          {deal.reference}
                        </span>
                      )}
                      {notProceeding && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 9999 }}>Not Proceeding</span>
                      )}
                      {isArchived && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 9999 }}>Archived</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {deal.client_email}
                      {deal.introducer_name && <span style={{ marginLeft: 10 }}>· via {deal.introducer_name}</span>}
                      {deal.created_by_name && perms.canViewTeamDeals && <span style={{ marginLeft: 10 }}>· {deal.created_by_name}</span>}
                    </div>
                  </div>

                  {/* RAG stage */}
                  <div style={{ flexShrink: 0 }}>
                    {deal.deal_stage_id && deal.deal_stage_colour ? (
                      <RAGBadge stageName={deal.deal_stage_name} ragColour={deal.deal_stage_colour} size="sm" />
                    ) : (
                      <span style={{ fontSize: 12, color: '#d1d5db' }}>No stage</span>
                    )}
                  </div>

                  {/* Commission stage */}
                  <div style={{ flexShrink: 0 }}>
                    <StageBadge stage={deal.stage} />
                  </div>

                  {/* Fee */}
                  <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 70 }}>
                    {gross > 0 ? (
                      <>
                        <div style={{ fontWeight: 700, color: '#10b981', fontSize: 15 }}>
                          £{net.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </div>
                        {hasNet && <div style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>net</div>}
                      </>
                    ) : (
                      <span style={{ color: '#d1d5db', fontSize: 14 }}>—</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ flexShrink: 0, display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => navigate(`/deal/${deal.id}`)}
                      style={{ border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 14px', fontSize: 12, cursor: 'pointer', background: '#fff', color: '#374151', fontWeight: 500 }}>
                      View
                    </button>
                    {perms.canEditDeal && (
                      <button onClick={() => navigate(`/deal/${deal.id}/edit`)}
                        style={{ border: '1px solid #10b981', borderRadius: 7, padding: '6px 14px', fontSize: 12, cursor: 'pointer', background: '#fff', color: '#10b981', fontWeight: 600 }}>
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination — list view only; board loads its own larger batch */}
      {viewMode === 'list' && meta.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '28px 0 8px' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: page === 1 ? '#f9fafb' : '#fff', color: page === 1 ? '#9ca3af' : '#0e1b2c', cursor: page === 1 ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: 13 }}>
            ← Prev
          </button>
          {Array.from({ length: Math.min(7, meta.pages) }, (_, i) => {
            const p = meta.pages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= meta.pages - 3 ? meta.pages - 6 + i : page - 3 + i;
            return (
              <button key={p} onClick={() => setPage(p)}
                style={{ width: 36, height: 36, border: '1px solid #e5e7eb', borderRadius: 8, background: p === page ? '#10b981' : '#fff', color: p === page ? '#fff' : '#0e1b2c', cursor: 'pointer', fontWeight: p === page ? 700 : 400, fontSize: 13 }}>
                {p}
              </button>
            );
          })}
          <button onClick={() => setPage(p => Math.min(meta.pages, p + 1))} disabled={page === meta.pages}
            style={{ padding: '8px 16px', border: '1px solid #e5e7eb', borderRadius: 8, background: page === meta.pages ? '#f9fafb' : '#fff', color: page === meta.pages ? '#9ca3af' : '#0e1b2c', cursor: page === meta.pages ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: 13 }}>
            Next →
          </button>
          <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 4 }}>{meta.total} total · page {page} of {meta.pages}</span>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </main>
  );

  if (noLayout) return _inner;
  return <div style={{ display: 'flex', minHeight: '100vh', background: '#f8f9fa' }}><Sidebar />{_inner}</div>;
}
